import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TextInput,
  TouchableOpacity,
  ActivityIndicator,
  KeyboardAvoidingView,
  Platform,
  Linking,
  useWindowDimensions,
} from 'react-native';
import { useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { collection, onSnapshot, orderBy, query, limit } from 'firebase/firestore';
import nativeFirestore from '@react-native-firebase/firestore';
import PantallaSegura from '../components/PantallaSegura';
import ScreenBackground from '../components/ScreenBackground';
import { useThemeColors } from '../constants/theme';
import { useAppStore } from '../store/useAppStore';
import { db } from '../services/firebaseConfig';
import { callPreguntarAlAsistente } from '../services/cloudFunctions';
import { mensajeDeError } from '../utils/mensajeDeError';
import { entrarALaApp } from '../utils/volver';
import { whatsappSoporte } from '../constants/contacto';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * AYUDA — el asistente que contesta con TUS datos
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Antes esto era un enlace al WhatsApp del fundador y nada más. Cada «¿por qué
 * no puedo conectarme?» acababa en el teléfono de una persona que además
 * conduce, y la respuesta tardaba lo que tardara.
 *
 * El asistente mira la cuenta de quien pregunta y contesta con lo que le pasa a
 * ÉL: «no puedes conectarte porque tu seguro venció el 12 de agosto». Lo que
 * toca dinero, multas o desbloquear una cuenta no lo contesta solo: se lo pasa
 * al fundador con el diagnóstico hecho, y él responde desde su teléfono.
 *
 * ── POR QUÉ LOS BOTONES DE ARRIBA ────────────────────────────────────────
 *
 * Quien abre esta pantalla suele estar molesto y a veces dentro del carro.
 * Escribir una frase entera en un teclado de móvil, con prisa, es la diferencia
 * entre preguntar y no preguntar. Los cuatro botones son las cuatro preguntas
 * que de verdad se repiten, y dejan la pregunta escrita de un toque.
 *
 * ── EL WHATSAPP SIGUE AHÍ ────────────────────────────────────────────────
 *
 * Abajo del todo, y a propósito. Quitarle a alguien el camino que ya conocía
 * —en una pantalla a la que llega con un problema— es cambiarle el problema por
 * dos. El asistente tiene que ganarse el sitio, no heredarlo.
 */

type Mensaje = {
  id: string;
  de: 'usuario' | 'asistente' | 'admin';
  texto: string;
  en: number | null;
};

const ATAJOS = [
  { icono: 'help-circle' as const, texto: '¿Por qué no puedo conectarme?' },
  { icono: 'document-text' as const, texto: '¿Cuándo aprueban mi verificación?' },
  { icono: 'shield-checkmark' as const, texto: '¿Cómo mando mi seguro nuevo?' },
  { icono: 'card' as const, texto: '¿Cómo pago mi membresía?' },
];

export default function AyudaScreen() {
  const router = useRouter();
  const colores = useThemeColors();
  const uid = useAppStore((s) => s.userFirebaseUid);
  // Ver la nota de `burbuja`: el ancho se calcula, no se deja en porcentaje.
  const { width: anchoPantalla } = useWindowDimensions();

  const [mensajes, setMensajes] = React.useState<Mensaje[]>([]);
  const [texto, setTexto] = React.useState('');
  const [enviando, setEnviando] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const scrollRef = React.useRef<ScrollView>(null);

  // El hilo se escucha EN VIVO: cuando el fundador contesta desde su teléfono,
  // la respuesta aparece sola sin que nadie recargue nada.
  //
  // ⚠️ SDK NATIVO EN EL MÓVIL, SDK WEB EN LA WEB. No es una preferencia de
  // estilo: cuando alguien entra por el camino de respaldo REST, el SDK web va
  // ANÓNIMO y la regla `isSelf(uid)` le deniega la lectura — la pantalla se
  // quedaría vacía sin decir por qué, justo en el APK que es lo que se publica.
  // `driver-subscription.tsx` y `order-receipt.tsx` ya hacen esta misma
  // separación por el mismo motivo.
  React.useEffect(() => {
    if (!uid) return;
    const recibir = (filas: Mensaje[]) => setMensajes(filas);
    const fallar = (e: any) => console.warn('[ayuda] no se pudo escuchar el hilo:', e?.message ?? e);

    if (Platform.OS !== 'web') {
      const quitarNativo = nativeFirestore()
        .collection('soporte_hilos')
        .doc(uid)
        .collection('mensajes')
        .orderBy('en', 'asc')
        .limit(100)
        .onSnapshot(
          (snap: any) =>
            recibir(
              (snap?.docs ?? []).map((d: any) => {
                const x = d.data() ?? {};
                return {
                  id: d.id,
                  de: x.de ?? 'asistente',
                  texto: String(x.texto ?? ''),
                  en: x.en?.toMillis?.() ?? null,
                };
              })
            ),
          fallar
        );
      return () => quitarNativo();
    }

    const q = query(
      collection(db, 'soporte_hilos', uid, 'mensajes'),
      orderBy('en', 'asc'),
      limit(100)
    );
    const quitar = onSnapshot(
      q,
      (snap) =>
        recibir(
          snap.docs.map((d) => {
            const x: any = d.data();
            return {
              id: d.id,
              de: x.de ?? 'asistente',
              texto: String(x.texto ?? ''),
              en: x.en?.toMillis?.() ?? null,
            };
          })
        ),
      fallar
    );
    return quitar;
  }, [uid]);

  React.useEffect(() => {
    const t = setTimeout(() => scrollRef.current?.scrollToEnd({ animated: true }), 120);
    return () => clearTimeout(t);
  }, [mensajes.length, enviando]);

  const preguntar = async (pregunta: string) => {
    const limpio = pregunta.trim();
    if (!limpio || enviando) return;
    if (!uid) {
      setError('Inicia sesión para poder escribirnos.');
      return;
    }
    setError(null);
    setTexto('');
    setEnviando(true);
    try {
      await callPreguntarAlAsistente({ texto: limpio });
      // No se añade nada a mano: el hilo llega por el listener de arriba, así
      // que lo que se ve es siempre lo que el servidor guardó de verdad.
    } catch (e: any) {
      console.warn('[ayuda] no se pudo preguntar:', e?.message);
      setError(mensajeDeError(e, 'No pudimos enviar tu pregunta. Revisa tu conexión.'));
      setTexto(limpio); // no se le pierde lo que escribió
    } finally {
      setEnviando(false);
    }
  };

  const burbuja = (m: Mensaje) => {
    const mio = m.de === 'usuario';
    const delDueno = m.de === 'admin';
    return (
      <View
        key={m.id}
        style={[
          estilos.burbuja,
          // ⚠️ ANCHO CALCULADO, NO `maxWidth: '88%'`.
          // Reportado en campo (2-oct-2026) con captura: la última palabra del
          // mensaje no se dibujaba — «…y te llega una» en vez de «…y te llega
          // una notificación». No lo recorta nada del código: son 152
          // caracteres guardados y se veían 138.
          // En Android, un `borderRadius` recorta a los hijos, y con un
          // `maxWidth` en PORCENTAJE el texto a veces se mide antes de que el
          // padre sepa su ancho: la altura se calcula con menos líneas de las
          // que acaban cabiendo y la última se queda fuera. Con un ancho en
          // píxeles esa carrera no existe.
          { maxWidth: Math.round((anchoPantalla - 32) * 0.88) },
          mio
            ? [estilos.mia, { backgroundColor: colores.primary }]
            : [estilos.suya, { backgroundColor: colores.card, borderColor: colores.border }],
        ]}
      >
        {!mio ? (
          <View style={estilos.quien}>
            <Ionicons
              name={delDueno ? 'person-circle' : 'chatbubble-ellipses'}
              size={14}
              color={delDueno ? '#2E7D32' : colores.textSecondary}
            />
            <Text style={[estilos.quienTexto, { color: delDueno ? '#2E7D32' : colores.textSecondary }]}>
              {delDueno ? 'Go Safely · respuesta de una persona' : 'Asistente'}
            </Text>
          </View>
        ) : null}
        <Text style={[estilos.textoBurbuja, { color: mio ? '#FFFFFF' : colores.text }]}>{m.texto}</Text>
      </View>
    );
  };

  return (
    <ScreenBackground>
      <PantallaSegura style={estilos.safe}>
        <View style={[estilos.cabecera, { borderBottomColor: colores.border }]}>
          <TouchableOpacity
            onPress={() => (router.canGoBack() ? router.back() : entrarALaApp(router, '/(tabs)/profile'))}
            style={estilos.volver}
            accessibilityRole="button"
            accessibilityLabel="Volver"
          >
            <Ionicons name="arrow-back" size={20} color={colores.primary} />
            <Text style={[estilos.volverTexto, { color: colores.primary }]}>Volver</Text>
          </TouchableOpacity>
          <Text style={[estilos.titulo, { color: colores.text }]}>Ayuda</Text>
        </View>

        <KeyboardAvoidingView
          style={{ flex: 1 }}
          // ⚠️ 'height' EN ANDROID, NO `undefined`.
          // Con `undefined` KeyboardAvoidingView no mueve nada: el teclado
          // sube y tapa la caja de escribir, asi que no se ve lo que uno esta
          // tecleando. En Android 'height' SI funciona porque el manifest
          // tiene `windowSoftInputMode="adjustResize"`.
          // El proyecto ya se habia topado con esto y lo dejo escrito en
          // src/app/chat.tsx; aqui se repitio el fallo.
          behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
          keyboardVerticalOffset={Platform.OS === 'ios' ? 8 : 0}
        >
          <ScrollView ref={scrollRef} contentContainerStyle={estilos.cuerpo} keyboardShouldPersistTaps="handled">
            {mensajes.length === 0 ? (
              <View style={[estilos.bienvenida, { backgroundColor: colores.card, borderColor: colores.border }]}>
                <Ionicons name="chatbubbles" size={30} color={colores.primary} />
                <Text style={[estilos.bienvenidaTitulo, { color: colores.text }]}>
                  Cuéntanos qué pasa
                </Text>
                <Text style={[estilos.bienvenidaTexto, { color: colores.textSecondary }]}>
                  Miro tu cuenta y te digo exactamente qué tienes pendiente. Lo que no pueda resolver yo
                  se lo paso al dueño, y te responde por aquí mismo.
                </Text>
              </View>
            ) : (
              mensajes.map(burbuja)
            )}

            {enviando ? (
              <View style={[estilos.burbuja, estilos.suya, { backgroundColor: colores.card, borderColor: colores.border }]}>
                <ActivityIndicator size="small" color={colores.primary} />
              </View>
            ) : null}

            {error ? (
              <Text style={[estilos.error, { color: '#D32F2F' }]}>{error}</Text>
            ) : null}
          </ScrollView>

          {mensajes.length === 0 ? (
            <View style={estilos.atajos}>
              {ATAJOS.map((a) => (
                <TouchableOpacity
                  key={a.texto}
                  onPress={() => preguntar(a.texto)}
                  style={[estilos.atajo, { backgroundColor: colores.card, borderColor: colores.border }]}
                  activeOpacity={0.7}
                  accessibilityRole="button"
                >
                  <Ionicons name={a.icono} size={16} color={colores.primary} />
                  <Text style={[estilos.atajoTexto, { color: colores.text }]}>{a.texto}</Text>
                </TouchableOpacity>
              ))}
            </View>
          ) : null}

          <View style={[estilos.barra, { borderTopColor: colores.border, backgroundColor: colores.background }]}>
            <TextInput
              value={texto}
              onChangeText={setTexto}
              placeholder="Escribe tu pregunta…"
              placeholderTextColor={colores.textSecondary}
              style={[estilos.campo, { color: colores.text, backgroundColor: colores.card, borderColor: colores.border }]}
              multiline
              maxLength={1500}
              editable={!enviando}
            />
            <TouchableOpacity
              onPress={() => preguntar(texto)}
              disabled={!texto.trim() || enviando}
              style={[
                estilos.enviar,
                { backgroundColor: !texto.trim() || enviando ? colores.border : colores.primary },
              ]}
              accessibilityRole="button"
              accessibilityLabel="Enviar la pregunta"
            >
              <Ionicons name="send" size={18} color="#FFFFFF" />
            </TouchableOpacity>
          </View>

          {/* El camino de siempre sigue abierto. Ver la nota de arriba. */}
          <TouchableOpacity
            onPress={() => Linking.openURL(whatsappSoporte('Hola, necesito ayuda con Go Safely.')).catch(() => {})}
            style={estilos.whatsapp}
            accessibilityRole="button"
          >
            <Ionicons name="logo-whatsapp" size={15} color="#25D366" />
            <Text style={[estilos.whatsappTexto, { color: colores.textSecondary }]}>
              ¿Prefieres WhatsApp? Escríbenos por ahí
            </Text>
          </TouchableOpacity>
        </KeyboardAvoidingView>
      </PantallaSegura>
    </ScreenBackground>
  );
}

const estilos = StyleSheet.create({
  safe: { flex: 1 },
  cabecera: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  volver: { flexDirection: 'row', alignItems: 'center', paddingVertical: 6, paddingRight: 12 },
  volverTexto: { fontSize: 16, marginLeft: 4, fontWeight: '600' },
  titulo: { fontSize: 17, fontWeight: '800' },
  cuerpo: { padding: 16, paddingBottom: 8, flexGrow: 1 },
  bienvenida: { borderRadius: 14, borderWidth: 1, padding: 18, alignItems: 'center', marginTop: 20 },
  bienvenidaTitulo: { fontSize: 17, fontWeight: '800', marginTop: 10, marginBottom: 6 },
  bienvenidaTexto: { fontSize: 14, lineHeight: 20, textAlign: 'center' },
  burbuja: { borderRadius: 14, padding: 12, marginBottom: 10 },
  mia: { alignSelf: 'flex-end', borderBottomRightRadius: 4 },
  suya: { alignSelf: 'flex-start', borderWidth: 1, borderBottomLeftRadius: 4 },
  quien: { flexDirection: 'row', alignItems: 'center', marginBottom: 5 },
  quienTexto: { fontSize: 11.5, fontWeight: '700', marginLeft: 4 },
  // `flexShrink: 1` para que el texto mande sobre la caja y no al reves: sin
  // el, un hijo de un contenedor flex puede quedarse sin el alto que necesita.
  textoBurbuja: { fontSize: 14.5, lineHeight: 21, flexShrink: 1 },
  error: { fontSize: 13, marginTop: 6, textAlign: 'center' },
  atajos: { paddingHorizontal: 16, paddingBottom: 6 },
  atajo: {
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 11,
    borderWidth: 1,
    paddingVertical: 11,
    paddingHorizontal: 13,
    marginBottom: 7,
  },
  atajoTexto: { fontSize: 14, marginLeft: 9, flex: 1 },
  barra: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    paddingHorizontal: 12,
    paddingTop: 10,
    paddingBottom: 6,
    borderTopWidth: StyleSheet.hairlineWidth,
  },
  campo: {
    flex: 1,
    borderWidth: 1,
    borderRadius: 12,
    paddingHorizontal: 13,
    paddingTop: 11,
    paddingBottom: 11,
    fontSize: 15,
    maxHeight: 120,
  },
  enviar: {
    width: 44,
    height: 44,
    borderRadius: 22,
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: 8,
  },
  whatsapp: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', paddingBottom: 12, paddingTop: 2 },
  whatsappTexto: { fontSize: 12.5, marginLeft: 6 },
});
